import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/context/NotificationContext";
import { updateEmployeeBusinessRole } from "@/api/updateEmployeeBusinessRole/updateEmployeeBusinessRoleApi";
import {
  CompanyEmployee,
  BusinessRoleOption,
} from "@/api/getEditCompanyDetails/types";

interface CompanyEmployeeListProps {
  employees: CompanyEmployee[];
  businessRoles: BusinessRoleOption[];
}

const CompanyEmployeeList: React.FC<CompanyEmployeeListProps> = ({
  employees: initialEmployees,
  businessRoles,
}) => {
  const { showNotification } = useNotification();
  const [employees, setEmployees] = useState(initialEmployees);
  const [updatingUuids, setUpdatingUuids] = useState<Set<string>>(new Set());

  const handleRoleChange = async (
    employeeUuid: string,
    newRoleId: string,
  ) => {
    const roleId = parseInt(newRoleId, 10);
    const role = businessRoles.find((r) => r.id === roleId);
    if (!role) return;

    const previousEmployees = [...employees];

    // Optimistic update
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.employeeUuid === employeeUuid
          ? { ...emp, businessRoleId: roleId, roleLabel: role.label }
          : emp,
      ),
    );

    setUpdatingUuids((prev) => new Set(prev).add(employeeUuid));

    try {
      await updateEmployeeBusinessRole(employeeUuid, { businessRoleId: roleId });
      showNotification(
        "Role Updated",
        `Business role updated successfully.`,
        "success",
      );
    } catch {
      // Revert on failure
      setEmployees(previousEmployees);
      showNotification(
        "Update Failed",
        "Failed to update business role. Please try again.",
        "error",
      );
    } finally {
      setUpdatingUuids((prev) => {
        const next = new Set(prev);
        next.delete(employeeUuid);
        return next;
      });
    }
  };

  if (employees.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No employees found for this company.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-slate-700">
            <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">
              Name
            </th>
            <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">
              Email
            </th>
            <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
              Role
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <tr
              key={employee.employeeUuid}
              className="border-b border-gray-100 dark:border-slate-700/50"
            >
              <td className="py-2 pr-4 text-gray-900 dark:text-white">
                {employee.firstName} {employee.lastName}
              </td>
              <td className="py-2 pr-4 text-gray-500 dark:text-gray-400">
                {employee.email}
              </td>
              <td className="py-2">
                <Select
                  disabled={updatingUuids.has(employee.employeeUuid)}
                  onValueChange={(value) =>
                    handleRoleChange(employee.employeeUuid, value)
                  }
                  value={employee.businessRoleId?.toString() ?? ""}
                >
                  <SelectTrigger className="w-[200px] h-8 text-sm">
                    <SelectValue placeholder="No role assigned" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessRoles.map((role) => (
                      <SelectItem
                        key={role.id}
                        value={role.id.toString()}
                      >
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyEmployeeList;
